import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AdminHeader } from "@/components/admin/AdminHeader";
import { DEAL_STAGES, SUBSCRIPTION_PLANS, SERVICE_GROUPS, formatSGD } from "@/lib/dealStages";
import { usePipelineOptions } from "@/lib/pipelineOptions";
import { formatDate } from "@/lib/format";
import { NewCustomerDialog } from "@/components/admin/NewCustomerDialog";
import { ImportCustomersDialog } from "@/components/admin/ImportCustomersDialog";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Search, RefreshCcw, Plus, Trash2, Upload, Users, X, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { MultiSelectFilter } from "@/components/admin/MultiSelectFilter";

export default function AdminPipeline() {
  const { stages, plans, serviceGroups, getStageMeta, getPlanMeta } = usePipelineOptions();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [stats, setStats] = useState({
    total: 0, by_stage: {}, value_by_stage_sgd: {}, open_value_sgd: 0, won_value_sgd: 0,
  });
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [ownerFilter, setOwnerFilter] = useState([]);
  const [stageFilter, setStageFilter] = useState([]);
  const [planFilter, setPlanFilter] = useState([]);
  const [serviceGroupFilter, setServiceGroupFilter] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [dealToDelete, setDealToDelete] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkOwnerId, setBulkOwnerId] = useState("");
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [bulkStage, setBulkStage] = useState("");
  const [bulkChangingStatus, setBulkChangingStatus] = useState(false);
  const [bulkPlan, setBulkPlan] = useState("");
  const [bulkChangingPlan, setBulkChangingPlan] = useState(false);
  const [bulkServiceGroup, setBulkServiceGroup] = useState("");
  const [bulkChangingServiceGroup, setBulkChangingServiceGroup] = useState(false);
  const [bulkField, setBulkField] = useState(""); // "" | "plan" | "status" | "pic" | "service_group"
  const [sort, setSort] = useState({ key: "updated_at", dir: "desc" });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      const [dealsRes, statsRes, teamRes] = await Promise.all([
        api.get("/admin/deals", { params }),
        api.get("/admin/deals/stats"),
        api.get("/admin/team").catch(() => ({ data: { items: [] } })),
      ]);
      setDeals(dealsRes.data.items || []);
      setStats(statsRes.data);
      setTeam(teamRes.data.items || []);
    } catch {
      toast.error("Failed to load pipeline");
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = useMemo(() => {
    let list = deals;
    if (stageFilter.length) list = list.filter((d) => stageFilter.includes(d.stage));
    if (planFilter.length) list = list.filter((d) => planFilter.includes(d.subscription_plan));
    if (serviceGroupFilter.length) list = list.filter((d) => serviceGroupFilter.includes(d.service_group));
    if (ownerFilter.length) list = list.filter((d) => ownerFilter.includes(d.owner_id));
    list = [...list];
    const { key, dir } = sort;
    if (!key) return list;
    const factor = dir === "asc" ? 1 : -1;
    const getValue = (d) => {
      switch (key) {
        case "title":
          return (d.title || "").toLowerCase();
        case "subscription_plan":
          return (getPlanMeta(d.subscription_plan)?.label || "").toLowerCase();
        case "service_group":
          return (d.service_group || "").toLowerCase();
        case "value_sgd":
          return Number(d.value_sgd) || 0;
        case "stage":
          return (getStageMeta(d.stage)?.label || "").toLowerCase();
        case "owner_name":
          return (d.owner_name || "").toLowerCase();
        case "updated_at":
          return d.updated_at || d.created_at || "";
        default:
          return "";
      }
    };
    list.sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);
      if (av < bv) return -1 * factor;
      if (av > bv) return 1 * factor;
      return 0;
    });
    return list;
  }, [deals, sort, stageFilter, planFilter, serviceGroupFilter, ownerFilter]);

  const toggleSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return { key: "updated_at", dir: "desc" }; // reset to default after third click
    });
  };

  // Clear selections whenever the visible dataset changes so we don't act on
  // rows that are no longer on-screen.
  useEffect(() => {
    setSelectedIds((prev) => {
      const nextIds = new Set(filtered.map((d) => d.id));
      const kept = new Set([...prev].filter((id) => nextIds.has(id)));
      return kept.size === prev.size ? prev : kept;
    });
  }, [filtered]);

  const allSelected = filtered.length > 0 && selectedIds.size === filtered.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(filtered.map((d) => d.id)));
  };

  const resetBulk = () => {
    setSelectedIds(new Set());
    setBulkOwnerId("");
    setBulkStage("");
    setBulkPlan("");
    setBulkServiceGroup("");
    setBulkField("");
  };

  const doBulkAssign = async () => {
    if (!bulkOwnerId || selectedIds.size === 0) return;
    const isUnassign = bulkOwnerId === "__unassigned__";
    setBulkAssigning(true);
    try {
      const { data } = await api.post("/admin/deals/bulk-assign", {
        deal_ids: [...selectedIds],
        owner_id: isUnassign ? null : bulkOwnerId,
      });
      const label = isUnassign ? "Unassigned" : `Reassigned to ${data.owner_name}`;
      toast.success(`${label} · ${data.updated_count} customer${data.updated_count === 1 ? "" : "s"}`);
      resetBulk();
      fetchAll();
    } catch (err) {
      toast.error("Failed to reassign");
    } finally {
      setBulkAssigning(false);
    }
  };

  const doBulkStatus = async () => {
    if (!bulkStage || selectedIds.size === 0) return;
    setBulkChangingStatus(true);
    try {
      const { data } = await api.post("/admin/deals/bulk-status", {
        deal_ids: [...selectedIds],
        stage: bulkStage,
      });
      const stageLabel = getStageMeta(data.stage)?.label || data.stage;
      toast.success(`Status → ${stageLabel} · ${data.updated_count} customer${data.updated_count === 1 ? "" : "s"}`);
      resetBulk();
      fetchAll();
    } catch (err) {
      toast.error("Failed to update status");
    } finally {
      setBulkChangingStatus(false);
    }
  };

  const doBulkPlan = async () => {
    if (!bulkPlan || selectedIds.size === 0) return;
    setBulkChangingPlan(true);
    try {
      const { data } = await api.post("/admin/deals/bulk-plan", {
        deal_ids: [...selectedIds],
        subscription_plan: bulkPlan,
      });
      const planLabel = getPlanMeta(data.subscription_plan)?.label || data.subscription_plan;
      toast.success(`Plan → ${planLabel} · ${data.updated_count} customer${data.updated_count === 1 ? "" : "s"}`);
      resetBulk();
      fetchAll();
    } catch (err) {
      toast.error("Failed to update plan");
    } finally {
      setBulkChangingPlan(false);
    }
  };

  const doBulkServiceGroup = async () => {
    if (!bulkServiceGroup || selectedIds.size === 0) return;
    const isClear = bulkServiceGroup === "__clear__";
    setBulkChangingServiceGroup(true);
    try {
      const { data } = await api.post("/admin/deals/bulk-service-group", {
        deal_ids: [...selectedIds],
        service_group: isClear ? "" : bulkServiceGroup,
      });
      const label = isClear ? "Cleared" : `Service Group → ${data.service_group}`;
      toast.success(`${label} · ${data.updated_count} customer${data.updated_count === 1 ? "" : "s"}`);
      resetBulk();
      fetchAll();
    } catch (err) {
      toast.error("Failed to update service group");
    } finally {
      setBulkChangingServiceGroup(false);
    }
  };

  const confirmDelete = async () => {
    if (!dealToDelete) return;
    try {
      await api.delete(`/admin/deals/${dealToDelete.id}`);
      setDeals((arr) => arr.filter((x) => x.id !== dealToDelete.id));
      toast.success("Customer removed from pipeline");
      const s = await api.get("/admin/deals/stats");
      setStats(s.data);
    } catch {
      toast.error("Failed to delete");
    } finally {
      setDealToDelete(null);
    }
  };

  const activeCount = deals.filter((d) => !["won", "lost"].includes(d.stage)).length;

  return (
    <div className="min-h-screen bg-brand-cream" data-testid="admin-pipeline-page">
      <AdminHeader />

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <p className="text-[10px] md:text-xs uppercase tracking-[0.45em] text-brand-orange font-semibold">Sales</p>
            <h1 className="text-3xl md:text-4xl font-semibold text-brand-navy mt-1" data-testid="admin-pipeline-heading">
              Pipeline
            </h1>
            <p className="mt-2 text-sm text-brand-slate/75">
              All customers in your pipeline. Click any row to view or edit the deal.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchAll}
              className="inline-flex items-center gap-2 border border-brand-navy/20 px-5 py-2.5 text-xs font-semibold text-brand-navy hover:bg-white rounded-full transition-colors"
              data-testid="pipeline-refresh-button"
            >
              <RefreshCcw size={14} /> Refresh
            </button>
            <button
              onClick={() => setShowImport(true)}
              className="inline-flex items-center gap-2 border border-brand-navy/20 bg-white px-5 py-2.5 text-xs font-semibold text-brand-navy hover:bg-brand-cream rounded-full transition-colors"
              data-testid="pipeline-import-button"
            >
              <Upload size={14} /> Import
            </button>
            <button
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 bg-brand-orange text-white px-5 py-2.5 text-xs font-semibold hover:bg-brand-orange-hover rounded-full transition-colors"
              data-testid="pipeline-new-customer-button"
            >
              <Plus size={14} /> New Customer
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Open Contract Value" value={formatSGD(stats.open_value_sgd)} accent testid="stat-open-value" />
          <StatCard label="Won Contract Value" value={formatSGD(stats.won_value_sgd)} testid="stat-won-value" />
          <StatCard label="Total Customers" value={stats.total || 0} testid="stat-total" />
          <StatCard label="Active Deals" value={activeCount} testid="stat-active" />
        </div>

        {/* Filters */}
        <div className="bg-white border border-brand-navy/10 p-4 md:p-5 mb-6 grid md:grid-cols-12 gap-3 rounded-2xl">
          <div className="md:col-span-4 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-slate/50" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search customer name…"
              className="pl-9 rounded-xl h-11"
              data-testid="pipeline-search-input"
            />
          </div>
          <div className="md:col-span-2">
            <MultiSelectFilter
              placeholder="All statuses"
              options={stages.map((s) => ({ value: s.value, label: s.label }))}
              selected={stageFilter}
              onChange={setStageFilter}
              testid="pipeline-filter-stage"
            />
          </div>
          <div className="md:col-span-2">
            <MultiSelectFilter
              placeholder="All plans"
              options={plans.map((p) => ({ value: p.value, label: p.label }))}
              selected={planFilter}
              onChange={setPlanFilter}
              testid="pipeline-filter-plan"
            />
          </div>
          <div className="md:col-span-2">
            <MultiSelectFilter
              placeholder="All service groups"
              options={serviceGroups.map((sg) => ({ value: sg, label: sg }))}
              selected={serviceGroupFilter}
              onChange={setServiceGroupFilter}
              testid="pipeline-filter-service-group"
            />
          </div>
          <div className="md:col-span-2">
            <MultiSelectFilter
              placeholder="All PICs"
              options={team.map((m) => ({ value: m.id, label: m.name || m.email }))}
              selected={ownerFilter}
              onChange={setOwnerFilter}
              testid="pipeline-filter-owner"
            />
          </div>
        </div>

        {/* List */}
        <div className="bg-white border border-brand-navy/10 overflow-hidden rounded-2xl">
          <div className="overflow-x-auto">
            <Table className="min-w-[1100px]">
              <TableHeader>
                <TableRow className="bg-brand-cream/60">
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={toggleAll}
                      aria-label="Select all"
                      className={someSelected ? "data-[state=unchecked]:bg-brand-orange/30" : ""}
                      data-testid="pipeline-select-all"
                    />
                  </TableHead>
                  <SortableHead label="Customer" sortKey="title" sort={sort} onToggle={toggleSort} testid="pipeline-sort-title" />
                  <SortableHead label="Plan" sortKey="subscription_plan" sort={sort} onToggle={toggleSort} testid="pipeline-sort-plan" />
                  <SortableHead label="Service Group" sortKey="service_group" sort={sort} onToggle={toggleSort} testid="pipeline-sort-service-group" />
                  <SortableHead label="Contract Value" sortKey="value_sgd" sort={sort} onToggle={toggleSort} testid="pipeline-sort-value" />
                  <SortableHead label="Status" sortKey="stage" sort={sort} onToggle={toggleSort} testid="pipeline-sort-stage" />
                  <SortableHead label="PIC" sortKey="owner_name" sort={sort} onToggle={toggleSort} testid="pipeline-sort-pic" />
                  <SortableHead label="Last Updated" sortKey="updated_at" sort={sort} onToggle={toggleSort} testid="pipeline-sort-updated" />
                  <TableHead className="text-brand-navy text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={9} className="text-center py-10 text-brand-slate/60">Loading…</TableCell></TableRow>
                )}
                {!loading && filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12 text-brand-slate/60" data-testid="pipeline-empty-state">
                      No customers yet. Click <strong>+ New Customer</strong> or convert a lead from the Leads page.
                    </TableCell>
                  </TableRow>
                )}
                {!loading && filtered.map((d) => {
                  const stage = getStageMeta(d.stage);
                  const plan = getPlanMeta(d.subscription_plan);
                  const isSelected = selectedIds.has(d.id);
                  return (
                    <TableRow
                      key={d.id}
                      onClick={() => navigate(`/admin/pipeline/${d.id}`)}
                      className={`cursor-pointer hover:bg-brand-cream/40 ${isSelected ? "bg-brand-orange/5" : ""}`}
                      data-testid={`pipeline-row-${d.id}`}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleRow(d.id)}
                          aria-label={`Select ${d.title}`}
                          data-testid={`pipeline-select-${d.id}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium text-brand-navy">{d.title}</TableCell>
                      <TableCell className="text-brand-slate">{plan?.label || "—"}</TableCell>
                      <TableCell className="text-brand-slate text-sm" data-testid={`pipeline-service-group-${d.id}`}>{d.service_group || "—"}</TableCell>
                      <TableCell className="text-brand-navy font-semibold">{formatSGD(d.value_sgd)}</TableCell>
                      <TableCell>
                        <Badge className={`${stage.color} rounded-full whitespace-nowrap`}>{stage.label}</Badge>
                      </TableCell>
                      <TableCell className="text-brand-slate text-sm">{d.owner_name || "—"}</TableCell>
                      <TableCell className="text-brand-slate text-sm whitespace-nowrap">
                        {formatDate(d.updated_at || d.created_at)}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => setDealToDelete(d)}
                          className="text-brand-slate/60 hover:text-red-600 p-1"
                          aria-label="Delete"
                          data-testid={`pipeline-delete-${d.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      </main>

      <NewCustomerDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        team={team}
        onCreated={(d) => {
          setDeals((arr) => [d, ...arr]);
          api.get("/admin/deals/stats").then((s) => setStats(s.data));
        }}
      />

      <ImportCustomersDialog
        open={showImport}
        onOpenChange={setShowImport}
        onImported={() => {
          fetchAll();
        }}
      />

      <ConfirmDialog
        open={!!dealToDelete}
        onOpenChange={(o) => !o && setDealToDelete(null)}
        title="Delete this customer?"
        description={dealToDelete ? `"${dealToDelete.title}" will be permanently removed from the pipeline. This cannot be undone.` : ""}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        destructive
        onConfirm={confirmDelete}
      />

      {selectedIds.size > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-brand-navy text-white rounded-3xl shadow-2xl border border-white/10 px-5 py-3 flex items-center gap-3 max-w-[calc(100vw-2rem)] flex-wrap justify-center"
          data-testid="pipeline-bulk-bar"
        >
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em]">
            <Users size={14} className="text-brand-orange" />
            <span data-testid="pipeline-bulk-count">{selectedIds.size} selected</span>
          </span>

          <div className="hidden sm:block w-px h-4 bg-white/20" />

          {/* Step 1: pick the field to update */}
          <label className="text-[10px] uppercase tracking-[0.2em] text-white/70 whitespace-nowrap">Update</label>
          <select
            value={bulkField}
            onChange={(e) => {
              const next = e.target.value;
              setBulkField(next);
              // Clear stale value pickers so only the relevant one is shown
              setBulkOwnerId("");
              setBulkStage("");
              setBulkPlan("");
              setBulkServiceGroup("");
            }}
            className="rounded-full text-xs bg-white text-brand-navy border border-transparent px-3 py-1.5 focus:outline-none"
            data-testid="pipeline-bulk-field"
          >
            <option value="">— pick a field —</option>
            <option value="plan" data-testid="pipeline-bulk-field-plan">Plan</option>
            <option value="status" data-testid="pipeline-bulk-field-status">Status</option>
            <option value="service_group" data-testid="pipeline-bulk-field-service-group">Service Group</option>
            <option value="pic" data-testid="pipeline-bulk-field-pic">PIC</option>
          </select>

          {/* Step 2: pick the value + apply, based on selected field */}
          {bulkField === "plan" && (
            <>
              <select
                value={bulkPlan}
                onChange={(e) => setBulkPlan(e.target.value)}
                className="rounded-full text-xs bg-white text-brand-navy border border-transparent px-3 py-1.5 focus:outline-none"
                data-testid="pipeline-bulk-plan"
              >
                <option value="">— pick a plan —</option>
                {plans.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
              <button
                onClick={doBulkPlan}
                disabled={!bulkPlan || bulkChangingPlan}
                className="inline-flex items-center gap-1 bg-brand-orange text-white px-4 py-1.5 text-xs font-semibold hover:bg-brand-orange-hover rounded-full disabled:opacity-50"
                data-testid="pipeline-bulk-plan-apply"
              >
                {bulkChangingPlan ? "Applying…" : "Update plan"}
              </button>
            </>
          )}

          {bulkField === "status" && (
            <>
              <select
                value={bulkStage}
                onChange={(e) => setBulkStage(e.target.value)}
                className="rounded-full text-xs bg-white text-brand-navy border border-transparent px-3 py-1.5 focus:outline-none"
                data-testid="pipeline-bulk-stage"
              >
                <option value="">— pick a status —</option>
                {stages.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              <button
                onClick={doBulkStatus}
                disabled={!bulkStage || bulkChangingStatus}
                className="inline-flex items-center gap-1 bg-brand-orange text-white px-4 py-1.5 text-xs font-semibold hover:bg-brand-orange-hover rounded-full disabled:opacity-50"
                data-testid="pipeline-bulk-status-apply"
              >
                {bulkChangingStatus ? "Applying…" : "Update status"}
              </button>
            </>
          )}

          {bulkField === "service_group" && (
            <>
              <select
                value={bulkServiceGroup}
                onChange={(e) => setBulkServiceGroup(e.target.value)}
                className="rounded-full text-xs bg-white text-brand-navy border border-transparent px-3 py-1.5 focus:outline-none"
                data-testid="pipeline-bulk-service-group"
              >
                <option value="">— pick a service group —</option>
                <option value="__clear__" data-testid="pipeline-bulk-service-group-clear">Clear (no service group)</option>
                {serviceGroups.map((sg) => (
                  <option key={sg} value={sg}>{sg}</option>
                ))}
              </select>
              <button
                onClick={doBulkServiceGroup}
                disabled={!bulkServiceGroup || bulkChangingServiceGroup}
                className="inline-flex items-center gap-1 bg-brand-orange text-white px-4 py-1.5 text-xs font-semibold hover:bg-brand-orange-hover rounded-full disabled:opacity-50"
                data-testid="pipeline-bulk-service-group-apply"
              >
                {bulkChangingServiceGroup ? "Applying…" : (bulkServiceGroup === "__clear__" ? "Clear" : "Update group")}
              </button>
            </>
          )}

          {bulkField === "pic" && (
            <>
              <select
                value={bulkOwnerId}
                onChange={(e) => setBulkOwnerId(e.target.value)}
                className="rounded-full text-xs bg-white text-brand-navy border border-transparent px-3 py-1.5 focus:outline-none"
                data-testid="pipeline-bulk-owner"
              >
                <option value="">— pick a team member —</option>
                <option value="__unassigned__" data-testid="pipeline-bulk-owner-unassigned">Unassigned (no owner)</option>
                {team.map((m) => (
                  <option key={m.id} value={m.id}>{m.name || m.email}</option>
                ))}
              </select>
              <button
                onClick={doBulkAssign}
                disabled={!bulkOwnerId || bulkAssigning}
                className="inline-flex items-center gap-1 bg-brand-orange text-white px-4 py-1.5 text-xs font-semibold hover:bg-brand-orange-hover rounded-full disabled:opacity-50"
                data-testid="pipeline-bulk-apply"
              >
                {bulkAssigning ? "Applying…" : (bulkOwnerId === "__unassigned__" ? "Unassign" : "Reassign")}
              </button>
            </>
          )}

          <button
            onClick={resetBulk}
            className="inline-flex items-center gap-1 text-white/70 hover:text-white text-xs"
            aria-label="Clear selection"
            data-testid="pipeline-bulk-clear"
          >
            <X size={14} /> Clear
          </button>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, accent, testid }) {
  return (
    <div
      className={`p-5 border bg-white rounded-2xl ${accent ? "border-brand-orange/40" : "border-brand-navy/10"}`}
      data-testid={testid}
    >
      <p className="text-[10px] uppercase tracking-[0.2em] text-brand-slate/65">{label}</p>
      <p className={`mt-2 text-2xl font-semibold ${accent ? "text-brand-orange" : "text-brand-navy"}`}>{value ?? 0}</p>
    </div>
  );
}

function SortableHead({ label, sortKey, sort, onToggle, testid }) {
  const active = sort.key === sortKey;
  const Icon = active ? (sort.dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <TableHead className="text-brand-navy select-none">
      <button
        type="button"
        onClick={() => onToggle(sortKey)}
        className={`inline-flex items-center gap-1.5 group hover:text-brand-orange transition-colors ${active ? "text-brand-orange" : ""}`}
        data-testid={testid}
        aria-label={`Sort by ${label}`}
      >
        <span>{label}</span>
        <Icon size={12} className={active ? "text-brand-orange" : "text-brand-slate/50 group-hover:text-brand-orange"} />
      </button>
    </TableHead>
  );
}
